import React, { useState, useEffect, useCallback } from 'react';
import { projectContactsAPI, customerAPI } from '../../services/api';
import './projectContacts.css';

// Manage the additional contacts attached to a job. Each contact can link an existing
// record from the contacts table, or be typed ad-hoc, and carries a "receives updates"
// flag that decides whether "send to all contacts" automations reach them.
const ProjectContacts = ({ projectId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Add form
  const [mode, setMode] = useState('link'); // 'link' (existing) | 'manual' (ad-hoc)
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null); // { id, label }
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', receivesUpdates: true });

  const load = useCallback(async () => {
    try {
      const res = await projectContactsAPI.list(projectId);
      setContacts(res.data || []);
    } catch (err) {
      console.error('Failed to load job contacts:', err);
      setError('Could not load contacts.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const runSearch = async (term) => {
    setSearch(term);
    setPicked(null);
    if (term.trim().length < 2) { setResults([]); return; }
    try {
      const res = await customerAPI.getAll({ search: term, pageSize: 25 });
      const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setResults(list);
    } catch (err) {
      console.error('Contact search failed:', err);
    }
  };

  const contactLabel = (c) =>
    `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.displayName || c.companyName || 'Unnamed';

  // entityKind: 0 Person, 1 Company, 2 Complex. Searching an agency returns the
  // agency and everyone who works there under near-identical labels, so say
  // which is which and who each person works for.
  const contactKind = (c) => (c.entityKind === 1 ? 'Company' : c.entityKind === 2 ? 'Property' : null);

  const contactSubLabel = (c) => {
    const kind = contactKind(c);
    const employer = c.companyName && contactLabel(c) !== c.companyName ? c.companyName : null;
    const contact = c.email || c.mobilePhone || c.mainPhone || c.phone || null;
    return [kind, employer, contact].filter(Boolean).join(' · ') || 'no contact info';
  };

  const resetForm = () => {
    setSearch(''); setResults([]); setPicked(null);
    setForm({ name: '', email: '', phone: '', role: '', receivesUpdates: true });
  };

  const handleAdd = async () => {
    setError('');
    const payload = {
      role: form.role.trim() || null,
      receivesUpdates: form.receivesUpdates,
    };
    if (mode === 'link') {
      if (!picked) { setError('Pick a contact to link.'); return; }
      payload.customerId = picked.id;
    } else {
      if (!form.name.trim() && !form.email.trim() && !form.phone.trim()) {
        setError('Enter a name, email, or phone.');
        return;
      }
      payload.name = form.name.trim() || null;
      payload.email = form.email.trim() || null;
      payload.phone = form.phone.trim() || null;
    }
    setSaving(true);
    try {
      const res = await projectContactsAPI.add(projectId, payload);
      setContacts((prev) => [...prev, res.data]);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add contact.');
    } finally {
      setSaving(false);
    }
  };

  const toggleUpdates = async (contact) => {
    try {
      const res = await projectContactsAPI.update(projectId, contact.id, {
        customerId: contact.customerId,
        name: contact.isLinked ? null : contact.name,
        email: contact.isLinked ? null : contact.email,
        phone: contact.isLinked ? null : contact.phone,
        role: contact.role,
        receivesUpdates: !contact.receivesUpdates,
      });
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? res.data : c)));
    } catch (err) {
      setError('Could not update contact.');
    }
  };

  const remove = async (contact) => {
    try {
      await projectContactsAPI.remove(projectId, contact.id);
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) {
      setError('Could not remove contact.');
    }
  };

  return (
    <div className="pc-wrapper">
      <p className="pc-intro">
        Contacts attached to this job. Anyone marked <strong>Receives updates</strong> is
        included when an automation is set to notify all job contacts.
      </p>

      {loading ? (
        <div className="pc-empty">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="pc-empty">No extra contacts yet. Add one below.</div>
      ) : (
        <ul className="pc-list">
          {contacts.map((c) => (
            <li key={c.id} className="pc-item">
              <div className="pc-item-main">
                <span className="pc-item-name">
                  {c.name || 'Unnamed'}
                  {c.role && <span className="pc-item-role">{c.role}</span>}
                  {!c.isLinked && <span className="pc-item-adhoc">ad-hoc</span>}
                </span>
                <span className="pc-item-contact">
                  {[c.email, c.phone].filter(Boolean).join(' · ') || 'no email or phone'}
                </span>
              </div>
              <label className="pc-updates-toggle" title="Receive automation updates">
                <input
                  type="checkbox"
                  checked={c.receivesUpdates}
                  onChange={() => toggleUpdates(c)}
                />
                Updates
              </label>
              <button className="pc-remove" onClick={() => remove(c)} title="Remove">×</button>
            </li>
          ))}
        </ul>
      )}

      <div className="pc-add">
        <div className="pc-mode">
          <button
            className={`pc-mode-btn${mode === 'link' ? ' active' : ''}`}
            onClick={() => { setMode('link'); setError(''); }}
          >
            Link existing
          </button>
          <button
            className={`pc-mode-btn${mode === 'manual' ? ' active' : ''}`}
            onClick={() => { setMode('manual'); setError(''); }}
          >
            Add manually
          </button>
        </div>

        {mode === 'link' ? (
          <div className="pc-link">
            {picked ? (
              <div className="pc-picked">
                <span>{picked.label}</span>
                <button onClick={() => { setPicked(null); setSearch(''); }}>change</button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="pc-input"
                  placeholder="Search a contact by name…"
                  value={search}
                  onChange={(e) => runSearch(e.target.value)}
                />
                {results.length > 0 && (
                  <ul className="pc-results">
                    {results.map((c) => (
                      <li key={c.id} onClick={() => { setPicked({ id: c.id, label: contactLabel(c) }); setResults([]); }}>
                        <span>{contactLabel(c)}</span>
                        <span className="pc-results-sub">{contactSubLabel(c)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="pc-manual">
            <input className="pc-input" placeholder="Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="pc-input" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="pc-input" placeholder="Phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        )}

        <div className="pc-add-row">
          <input className="pc-input pc-role" placeholder="Role (e.g. On-Call, Managing PM)"
            value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          <label className="pc-updates-toggle">
            <input type="checkbox" checked={form.receivesUpdates}
              onChange={(e) => setForm({ ...form, receivesUpdates: e.target.checked })} />
            Receives updates
          </label>
          <button className="pc-add-btn" onClick={handleAdd} disabled={saving}>
            {saving ? 'Adding…' : 'Add contact'}
          </button>
        </div>
      </div>

      {error && <div className="pc-error">{error}</div>}
    </div>
  );
};

export default ProjectContacts;
